import React from "react";
import get from "lodash/get";

import {
  ContentType,
  DestinationType,
  TWITTER_MAX_CHAR_LIMIT,
} from "../../constants";
import { trim } from "../helpers";

export async function getPublishingProblems(
  destination: Destination,
  content: Content[]
): Promise<[string[], string[]]> {
  const problems: string[] = [];
  const warnings: string[] = [];

  const texts = content.filter((c) => c.type === ContentType.TEXT);
  const files = content.filter((c) => c.type === ContentType.FILE);
  const photos = files.filter((c) =>
    (c.meta?.contentType || "").match(/^image/)
  );
  const videos = files.filter((c) =>
    (c.meta?.contentType || "").match(/^video/)
  );
  const audios = files.filter((c) =>
    (c.meta?.contentType || "").match(/^audio/)
  );

  /* **************************************************************************** TWITTER */

  if (destination && destination.type === DestinationType.TWITTER) {
    if (texts.length >= 2) {
      warnings.push(
        `You have selected more than one text items. Only the first one (\"${trim(
          texts[0].value,
          30
        )}\") will be used.`
      );
    }
    if (get(texts, "[0].value.length", 0) > TWITTER_MAX_CHAR_LIMIT) {
      warnings.push(
        `You may have problems posting this text. It is ${texts[0].value.length} characters long. The limit is ${TWITTER_MAX_CHAR_LIMIT}.`
      );
    }
    if (audios.length > 0 || videos.length > 0) {
      warnings.push(
        `Contempas doesn't support uploading audio or video to Twitter. Instead a link to the file will be added as part of the Tweet.`
      );
    }
    if (photos.length > 4) {
      warnings.push(
        `maximum of 4 photos are allowed. The rest will be ignored.`
      );
    }
  }

  /* **************************************************************************** FACEBOOK */

  if (destination && destination.type === DestinationType.FACEBOOK) {
    if (photos.length > 0 && videos.length > 0) {
      warnings.push(
        "Facebook does not allow a mix between photos and videos. You'll end up having just the video published."
      );
    }
    if (videos.length > 1) {
      warnings.push(
        "Publishing multiple video files is not supported. You'll have only the first one published."
      );
    }
    if (audios.length > 0) {
      warnings.push(
        `Contempas doesn't support uploading audio to Facebook. The file will be ignored`
      );
    }
  }

  /* **************************************************************************** INSTAGRAM */

  if (destination && destination.type === DestinationType.INSTAGRAM) {
    if (videos.length > 0) {
      warnings.push(
        <>
          You are about to publish video to Instagram. Check out the
          requirements{" "}
          <a
            href="https://developers.facebook.com/docs/instagram-api/reference/ig-user/media#create-video-container"
            target="_blank"
          >
            here
          </a>
          .
        </>
      );
    }
    if (photos.length > 0 && videos.length > 0) {
      warnings.push(
        "Instagram API does not allow a mix between photos and videos. You'll end up having two different posts."
      );
    }
    if (photos.length > 1) {
      warnings.push(
        "Publishing multiple photos is not supported. You'll have only the first one published."
      );
    }
    if (videos.length > 1) {
      warnings.push(
        "Publishing multiple video files is not supported. You'll have only the first one published."
      );
    }
    if (photos.length > 0) {
      if (!(await checkInstagramRatio(photos[0]))) {
        warnings.push(`Image with invalid aspect ratio.`);
      }
    }
    if (audios.length > 0) {
      warnings.push(
        `Contempas doesn't support uploading audio to Instagram. The file will be ignored.`
      );
    }
  }

  /* **************************************************************************** YOUTUBE */

  if (destination && destination.type === DestinationType.YOUTUBE) {
    if (videos.length > 1) {
      warnings.push(
        "Publishing multiple video files is not supported. You'll have only the first one published."
      );
    }
    if (texts.length >= 2) {
      warnings.push(
        `You have selected more than one text items. Only the first one (\"${trim(
          texts[0].value,
          30
        )}\") will be used.`
      );
    }
    if (photos.length > 0) {
      warnings.push(
        `You can upload only a video + text. The photos will be ignored.`
      );
    }
    if (audios.length > 0) {
      warnings.push(
        `Contempas doesn't support uploading audio to YouTube. Those files will be ignored.`
      );
    }
  }

  /* **************************************************************************** MAILCHIMP */

  if (destination && destination.type === DestinationType.MAILCHIMP) {
    if (videos.length > 0 || audios.length > 0) {
      warnings.push(
        "Contempas doesn't support injecting videos or audio to the email. Just a link to the file will be added."
      );
    }
  }

  /* **************************************************************************** LinkedIn */

  if (destination && destination.type === DestinationType.LINKEDIN) {
    if (audios.length > 0 || videos.length > 0) {
      warnings.push(
        `Contempas doesn't support uploading audio or video to LinkedIn. Instead a link to the file will be added at the end of the post.`
      );
    }
    if (texts.length >= 2) {
      warnings.push(
        `You have selected more than one text items. Only the first one (\"${trim(
          texts[0].value,
          30
        )}\") will be used.`
      );
    }
    if (photos.length >= 2) {
      warnings.push(
        `You have selected more than one image. Only the first one will be used.`
      );
    }
  }

  return [problems, warnings];
}

async function checkInstagramRatio(photo: Content): Promise<boolean> {
  // Instagram official ratio - between 1.91:1 (1.91) and 4:5 (0.8)
  const range = [0.7, 2];
  const url = photo.meta?.url;
  if (url) {
    return await new Promise((done) => {
      const img = new Image();
      let alreadyDone = false;
      const interval = setTimeout(() => {
        done(false);
        alreadyDone = true;
      }, 6000);
      img.onload = function () {
        try {
          clearTimeout(interval);
          let ratio =
            this.width < this.height
              ? this.width / this.height
              : this.height / this.width;
          if (!alreadyDone) {
            if (ratio < range[0] || ratio > range[1]) {
              done(false);
            } else {
              done(true);
            }
          }
        } catch (err) {
          console.log(err);
          if (!alreadyDone) {
            done(false);
          }
        }
      };
      img.src = url;
    });
  }
  return false;
}
